// src/theme/config.ts
export interface ComponentParams {
    [key: string]: Record<string, unknown>; // chaque composant peut avoir ses propres paramètres
}

export interface Modules {
    [key: string]: boolean; // chaque module activé/désactivé
}

// Définition du layout responsive piloté par la config
export type BreakpointName = 'xs' | 'sm' | 'md' | 'lg' | 'xl';

export interface BreakpointDef {
    name: BreakpointName;
    minWidth: number; // en px
    columns: number;  // nombre de colonnes pour cette largeur
}

export interface GridPosition {
    colStart?: number;
    colEnd?: number;
    rowStart?: number;
    rowEnd?: number;
}

export interface LayoutItem {
    id: string; // identifiant unique pour aider au keying React
    component: string; // nom du composant dans le registre
    enabled?: boolean;
    outsideGrid?: boolean; // si true, rendu hors de la grille
    props?: Record<string, unknown>;
    // positions par breakpoint; si absent -> auto-placement
    positions?: Partial<Record<BreakpointName, GridPosition>>;
    page?: string; // URL de la page (si absent, visible sur toutes les pages)
}

export interface GridLayoutConfig {
    breakpoints: BreakpointDef[]; // trié par minWidth croissant
    gap?: number; // gap horizontal en px
    rowGap?: number; // gap vertical en px (prioritaire sur gap si défini)
    items: LayoutItem[]; // éléments à afficher
    overlay?: {
        enabled: boolean;
        showColumns?: boolean;
        showRows?: boolean;
        color?: string; // couleur des lignes
        opacity?: number; // 0..1
        rowHeight?: number; // hauteur entre lignes (overlay) en px
    };
}
export interface Page {
    pageName: string;
    pageUrl: string;
    InNavBar: boolean;
    description: string;
    gridLayout?: GridLayoutConfig; // Chaque page peut avoir sa propre grille
    pageSpecificModules?: Modules; // Modules spécifiques à cette page
    pageSpecificParams?: ComponentParams; // Paramètres spécifiques à cette page
}

// Configuration de l'API
export interface ApiConfig {
    baseUrl: string; // URL de base de l'API
    readToken: string; // Token pour les opérations de lecture (GET)
    writeToken: string; // Token pour les opérations d'écriture (POST, PUT, DELETE)
    enableSync: boolean; // Activer la synchronisation avec l'API
    autoLoadParams: boolean; // Charger automatiquement les paramètres depuis l'API
    media?: MediaConfig; // Configuration des médias (optionnelle)
}

// Configuration du lazy loading
export interface LazyLoadingConfig {
    enabled: boolean;
    basePreloadDistance: number;     // Distance de base pour le préchargement (px)
    maxParallelLoads: number;        // Nombre max de chargements parallèles
    pauseThreshold: number;          // Seuil de charge pour pause (0-1)
    importanceWeights: {
        visibility: number;          // Poids de la visibilité (0-1)
        size: number;                // Poids de la taille (0-1)
        position: number;            // Poids de la position (0-1)
        type: number;                // Poids du type de média (0-1)
    };
}

// Configuration du cache des médias
export interface MediaCacheConfig {
    enabled: boolean;      // Activer/désactiver le cache (défaut: true)
    enableLogs: boolean;   // Activer/désactiver les logs (défaut: false)
    maxStorageSize?: number; // Taille max en MB (optionnel)
}

// Configuration des médias
export interface MediaConfig {
    lazyLoadThreshold: string;
    enableProgressiveLoading: boolean;
    apiTimeout: number;
    defaultQuality: number; // Qualité par défaut (0-95%)
    minQuality?: number; // Qualité minimale (défaut: 1)
    maxQuality?: number; // Qualité maximale (défaut: 95)
    lazyLoading?: LazyLoadingConfig; // Configuration du lazy loading
    cache?: MediaCacheConfig; // Configuration du cache
}

// Configuration du mode développeur (outils d'édition/overlay, persistance locale, etc.)
export interface DevModeConfig {
    enabled: boolean;
}

export interface Config {
    primaryColor: string;
    secondaryColor: string;
    accentuatedColor: string;
    darkPrimaryColor: string;
    darkSecondaryColor: string;
    darkAccentuatedColor: string;
    fontFamily: string;
    darkModeEnabled: boolean;
    isDark: boolean;
    modules: Modules;
    componentParams: ComponentParams;
    assetsBasePath: string;
    SiteName: string;
    PagesList: Page[];
    gridLayout: GridLayoutConfig;
    api: ApiConfig; // Configuration de l'API
    devMode?: DevModeConfig;
}

const config = {
    "primaryColor": "#0020ff",
    "secondaryColor": "#040404",
    "accentuatedColor": "#326120",
    "darkPrimaryColor": "rgb(0,0,0)",
    "darkSecondaryColor": "#7c3aed",
    "darkAccentuatedColor": "#ff0000",
    "fontFamily": "system-ui, Avenir, Helvetica, Arial, sans-serif",
    "darkModeEnabled": true,
    "isDark": true,
    "devMode": {
        "enabled": false
    },
    "modules": {
        "Glass_NavBar": true,
        "ImageModule": true,
        "VideoModule": true,
        "TemplateComponent": true
    },
    "componentParams": {
        "Glass_NavBar": {
            "tinted": false
        },
        "ImageModule": {
            "src": "/vite.svg",
            "alt": "Image exemple",
            "loadOriginal": true
        },
        "VideoModule": {
            "controls": true,
            "autoPlay": false
        },
        "TemplateComponent": {
            "title": "Template",
            "backgroundColor": "rgba(0, 123, 255, 0.1)",
            "fontSize": 14,
            "showBorder": true
        }
    },
    "assetsBasePath": "./public",
    "SiteName": "Luc",
    "PagesList": [
        {
            "pageName": "Accueil",
            "pageUrl": "/",
            "InNavBar": true,
            "description": "Page d'accueil du site"
        },
        {
            "pageName": "Page 2",
            "pageUrl": "/AboutUs",
            "InNavBar": true,
            "description": "Nouvelle page",
            "gridLayout": {
                "breakpoints": [
                    {
                        "name": "xs",
                        "minWidth": 0,
                        "columns": 1
                    },
                    {
                        "name": "sm",
                        "minWidth": 640,
                        "columns": 2
                    },
                    {
                        "name": "md",
                        "minWidth": 768,
                        "columns": 3
                    },
                    {
                        "name": "lg",
                        "minWidth": 1024,
                        "columns": 4
                    },
                    {
                        "name": "xl",
                        "minWidth": 1280,
                        "columns": 17
                    }
                ],
                "gap": 0,
                "rowGap": 0,
                "overlay": {
                    "enabled": false,
                    "showColumns": true,
                    "showRows": true,
                    "color": "#ff0000",
                    "opacity": 0.5
                },
                "items": [
                    {
                        "id": "Image-d-accueil-de-la-page",
                        "component": "ImageModule",
                        "enabled": true,
                        "outsideGrid": false,
                        "page": "/AboutUs",
                        "props": {
                            "src": "/vite.svg",
                            "alt": "Image exemple",
                            "loadOriginal": true
                        },
                        "positions": {
                            "xl": {
                                "colStart": 6,
                                "colEnd": 13,
                                "rowStart": 2,
                                "rowEnd": 7
                            },
                            "lg": {
                                "colStart": 2,
                                "colEnd": 4,
                                "rowStart": 1,
                                "rowEnd": 3
                            },
                            "md": {
                                "colStart": 1,
                                "colEnd": 4,
                                "rowStart": 1,
                                "rowEnd": 3
                            },
                            "sm": {
                                "colStart": 1,
                                "colEnd": 3,
                                "rowStart": 1,
                                "rowEnd": 3
                            }
                        }
                    },
                    {
                        "id": "Bar-de-navigation",
                        "component": "Glass_NavBar",
                        "enabled": true,
                        "outsideGrid": true,
                        "page": "/AboutUs",
                        "props": {
                            "tinted": false
                        },
                        "positions": {}
                    },
                    {
                        "id": "Composant-générique",
                        "component": "TemplateComponent",
                        "enabled": true,
                        "outsideGrid": false,
                        "page": "/AboutUs",
                        "props": {
                            "title": "Template",
                            "backgroundColor": "rgba(0, 123, 255, 0.1)",
                            "fontSize": 14,
                            "showBorder": true
                        },
                        "positions": {
                            "xl": {
                                "colStart": 6,
                                "colEnd": 13,
                                "rowStart": 7,
                                "rowEnd": 12
                            },
                            "lg": {
                                "colStart": 2,
                                "colEnd": 4,
                                "rowStart": 3,
                                "rowEnd": 5
                            },
                            "md": {
                                "colStart": 1,
                                "colEnd": 4,
                                "rowStart": 3,
                                "rowEnd": 5
                            },
                            "sm": {
                                "colStart": 1,
                                "colEnd": 3,
                                "rowStart": 3,
                                "rowEnd": 5
                            }
                        }
                    }
                ]
            },
            "pageSpecificModules": {},
            "pageSpecificParams": {}
        }
    ],
    "gridLayout": {
        "breakpoints": [
            {
                "name": "xs",
                "minWidth": 0,
                "columns": 1
            },
            {
                "name": "sm",
                "minWidth": 640,
                "columns": 2
            },
            {
                "name": "md",
                "minWidth": 768,
                "columns": 3
            },
            {
                "name": "lg",
                "minWidth": 1024,
                "columns": 4
            },
            {
                "name": "xl",
                "minWidth": 1280,
                "columns": 17
            }
        ],
        "gap": 0,
        "rowGap": 0,
        "overlay": {
            "enabled": false,
            "showColumns": true,
            "showRows": true,
            "color": "#ff0000",
            "opacity": 0.5
        },
        "items": [
            {
                "id": "Bar-de-navigation",
                "component": "Glass_NavBar",
                "enabled": true,
                "outsideGrid": true,
                "page": "/",
                "props": {
                    "tinted": false
                },
                "positions": {}
            },
            {
                "id": "Image-d-accueil-du-site",
                "component": "ImageModule",
                "enabled": true,
                "outsideGrid": false,
                "page": "/",
                "props": {
                    "src": "/vite.svg",
                    "alt": "Image exemple",
                    "loadOriginal": true
                },
                "positions": {
                    "xl": {
                        "colStart": 6,
                        "colEnd": 13,
                        "rowStart": 4,
                        "rowEnd": 8
                    },
                    "lg": {
                        "colStart": 2,
                        "colEnd": 4,
                        "rowStart": 1,
                        "rowEnd": 3
                    },
                    "md": {
                        "colStart": 1,
                        "colEnd": 4,
                        "rowStart": 1,
                        "rowEnd": 3
                    },
                    "sm": {
                        "colStart": 1,
                        "colEnd": 3,
                        "rowStart": 1,
                        "rowEnd": 3
                    }
                }
            },
            {
                "id": "Video-d-accueil-du-site",
                "component": "VideoModule",
                "enabled": true,
                "outsideGrid": false,
                "page": "/",
                "props": {
                    "controls": true,
                    "autoPlay": false
                },
                "positions": {
                    "xl": {
                        "colStart": 6,
                        "colEnd": 13,
                        "rowStart": 8,
                        "rowEnd": 12
                    },
                    "lg": {
                        "colStart": 2,
                        "colEnd": 4,
                        "rowStart": 3,
                        "rowEnd": 5
                    },
                    "md": {
                        "colStart": 1,
                        "colEnd": 4,
                        "rowStart": 3,
                        "rowEnd": 5
                    },
                    "sm": {
                        "colStart": 1,
                        "colEnd": 3,
                        "rowStart": 3,
                        "rowEnd": 5
                    }
                }
            }
        ]
    },
    "api": {
        "baseUrl": "https://api.g-and-c.be",
        "readToken": "JLB4IwUqndrmaadQR9pQv1xrxK2Fxu8P",
        "writeToken": "4aPEnPDzPNHtBenlTz2rzZRdSel8gRdN",
        "enableSync": true,
        "autoLoadParams": true,
        "media": {
            "lazyLoading": {
                "enabled": true,
                "basePreloadDistance": 800,
                "maxParallelLoads": 2,
                "pauseThreshold": 0.7,
                "importanceWeights": {
                    "visibility": 0.4,
                    "size": 0.3,
                    "position": 0.2,
                    "mediaType": 0.1
                }
            },
            "cache": {
                "enabled": true,
                "enableLogs": false,
                "maxStorageSize": 500
            }
        }
    }
};

export default config;
